import React, { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import Icon from "../../components/icon/Icon";
import { getPhoneConfig } from "./phoneCountryConfig";

interface PhoneNumberInputProps {
  name: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  hideRequiredMark?: boolean;
  defaultValue?: string;
  placeholder?: string;
}

const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  name,
  label = "Phone Number",
  required = false,
  disabled = false,
  hideRequiredMark = false,
  defaultValue,
  placeholder,
}) => {
  const {
    register,
    setValue,
    formState: { errors },
    control,
  } = useFormContext();

  const countryName = useSelector(
    (state: any) => state?.currentUser?.data?.country?.countryName
  );

  const config = useMemo(() => getPhoneConfig(countryName), [countryName]);

  const watched = useWatch({ control, name }) as string | undefined;

  useEffect(() => {
    if (watched == null || watched === "") return;
    const digits = String(watched).replace(/\D/g, "");
    const normalized =
      digits.length > config.digitLength
        ? digits.slice(-config.digitLength)
        : digits;
    if (normalized !== watched) {
      setValue(name, normalized, { shouldValidate: false, shouldDirty: false });
    }
  }, [watched, config.digitLength, name, setValue]);

  const errorMessage = getErrorMessage(errors, name);

  return (
    <div className="form-group">
      {label && (
        <div className="form-label-wrapper">
          <label className="form-label" htmlFor={name}>
            {label}
            {required && !hideRequiredMark && (
              <span className="mandatory">*</span>
            )}
          </label>
        </div>
      )}
      <div className="form-control-wrap">
        <div className="input-group">
          <span
            className="input-group-text"
            style={{
              background: "#f5f6fa",
              color: "#526484",
              fontWeight: 500,
              borderRight: 0,
            }}
          >
            {config.dialCode}
            <Icon
              name="lock-alt"
              style={{ marginLeft: 6, fontSize: 12, opacity: 0.6 }}
            />
          </span>
          <input
            type="text"
            id={name}
            className="form-control"
            placeholder={placeholder ?? `${"9".repeat(config.digitLength)}`}
            maxLength={config.digitLength}
            disabled={disabled}
            defaultValue={defaultValue}
            autoComplete="off"
            {...register(name, {
              required: required ? "This field is required" : undefined,
              pattern: {
                value: config.pattern,
                message: config.errorMessage,
              },
            })}
            onInput={(e) => {
              const el = e.currentTarget;
              el.value = el.value
                .replace(/\D/g, "")
                .slice(0, config.digitLength);
            }}
          />
        </div>
        {errorMessage && <p className="mandatory">{errorMessage}</p>}
      </div>
    </div>
  );
};

function getErrorMessage(errors: any, fieldName: string): string | undefined {
  const parts = fieldName.split(/[\[\].]+/).filter(Boolean);
  let current: any = errors;
  for (const part of parts) {
    if (current && typeof current === "object") current = current[part];
    else return undefined;
  }
  if (current && typeof current.message === "string") return current.message;
  return undefined;
}

export default PhoneNumberInput;
